/* emailClient.js — Phase 1 outbound email helper.
 *
 * One thin wrapper around POST /api/email/send that all 5 operational
 * sheets share. Returns:
 *
 *   { ok: true,  email_id, activity_id, recipient_count, delivered_summary }
 *   { ok: false, error_code, error_message, draft_preserved: true }
 *
 * Callers MUST preserve the user's draft on `ok === false` so the agent
 * can retry without re-typing.
 */
import { api } from "./api";

/**
 * @param {{
 *   dealId: string,
 *   recipients: Array<{email: string, full_name?: string, role?: string}>,
 *   subject: string,
 *   body: string,
 *   kind?: "chase" | "request" | "seller_update" | "broadcast" | "email",
 *   titleOverride?: string,
 * }} payload
 */
export async function sendOperationalEmail({
  dealId, recipients, subject, body,
  kind = "email", titleOverride = null,
}) {
  try {
    const { data } = await api.post("/api/email/send", {
      deal_id: dealId,
      recipients: (recipients || [])
        .filter((r) => r?.email)
        .map((r) => ({ email: r.email, full_name: r.full_name || r.name, role: r.role || r.participant_type })),
      subject,
      body,
      kind,
      title_override: titleOverride,
    });
    return {
      ok: true,
      emailId: data?.email_id || null,
      activityId: data?.activity_id || null,
      recipientCount: data?.recipient_count || 0,
      deliveredSummary: data?.delivered_summary || `Email sent to ${recipients?.length || 0} participants`,
    };
  } catch (err) {
    const detail = err?.response?.data?.detail;
    const code = typeof detail === "object" ? detail?.code : err?.response?.status;
    const message = (typeof detail === "object" ? detail?.message : detail)
      || err?.message
      || "Email delivery failed";
    return {
      ok: false,
      errorCode: code || "unknown_error",
      errorMessage: typeof message === "string" ? message : "Email delivery failed",
    };
  }
}

/** Recipients on a deal that have a valid email. Used to short-circuit
 *  "send" when there's nobody to send to. */
export function emailableRecipients(participants) {
  return (participants || []).filter((p) => p?.email && /\S+@\S+/.test(p.email));
}
